import React, { useEffect, useMemo, useState } from "react";

import { Button, Stack } from "@mui/material";
import { ClientEntityAutocomplete } from "components/Autocompletes/ClientEntityAutocomplete";
import { useActionHook } from "hooks";
import { useClientActions, useClientIdSelector, useDealActions, useInvestorsSelector } from "store";

type InvestorOption = { id: number; name: string };

interface ClientInvestorPromptResult {
  investor: { id: number; name: string } | null;
}

interface Props {
  currentInvestorId: number | null;
  onSubmit: (result: ClientInvestorPromptResult) => void;
  onCancel: () => void;
}

/**
 * Client-workspace investor picker. Two sections (styled like `InvestorAutocomplete`):
 *  - "Client Entity"     — investors allocated to this client (GET /clients/{id}/allocations)
 *  - "Account Investors" — every other investor in the account
 * Single-select; the chosen investor's id is written to the file's `investor_id`.
 */
export const ClientInvestorPrompt: React.FC<Props> = ({ currentInvestorId, onSubmit, onCancel }) => {
  const clientId = useClientIdSelector();
  const { getAllocations } = useClientActions();
  const { getInvestorsAction } = useDealActions();
  const investors = useInvestorsSelector();
  const [selected, setSelected] = useState<InvestorOption | null>(() => {
    return investors.find((inv) => inv.id === currentInvestorId) ?? null;
  });

  const {
    data: allocations = [],
    action: fetchAllocations,
    loading
  } = useActionHook(() => (clientId ? getAllocations(clientId, true) : Promise.resolve([])));
  const clientInvestors = useMemo(() => {
    return allocations.map((allocation) => ({
      id: allocation.investor_id,
      name: allocation.name
    }));
  }, [allocations]);

  useEffect(() => {
    if (clientId) fetchAllocations();
    if (!investors.length) getInvestorsAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <Stack sx={{ padding: "24px", gap: "20px", minWidth: "427px" }}>
      <ClientEntityAutocomplete
        loading={loading}
        clientInvestors={clientInvestors}
        preselectedInvestorId={selected?.id ?? null}
        onChange={(value) => {
          setSelected(value);
        }}
        label="Attributed to"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "& fieldset": { borderColor: "#7b92a9" }
          }
        }}
      />

      <Stack direction="row" justifyContent="flex-end" gap="8px">
        <Button
          onClick={onCancel}
          sx={{
            borderRadius: "32px",
            border: "1px solid #7b92a9",
            color: "#071f35",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px"
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit({ investor: selected ? { id: selected.id, name: selected.name } : null })}
          sx={{
            borderRadius: "32px",
            bgcolor: "#071f35",
            color: "white",
            textTransform: "none",
            padding: "4px 16px",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            "&:hover": { bgcolor: "#0a2a45" },
            "&:disabled": { bgcolor: "#bac7ce", color: "white" }
          }}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};
